<template>
  <div class="container">
    <div class="dropdown-container">
      <!-- FSM -->
      <div class="dropdown-item">
        <label for="fsmSelect">FSM</label>
        <select id="fsmSelect" v-model="selectedFsm" @change="onFsmChange" :disabled="isFsmLocked">
          <option value="">Select FSM</option>
          <option v-if="isOtherUser" :value="ALL">All FSMs</option>
          <option v-for="fsm in fsmList" :key="fsm.profile_id" :value="String(fsm.profile_id)">
            {{ fsm.name }}
          </option>
        </select>
      </div>

      <!-- ASM -->
      <div class="dropdown-item">
        <label for="asmSelect">ASM</label>
        <select id="asmSelect" v-model="selectedAsm" @change="onAsmChange" :disabled="isAsmLocked">
          <option value="">Select ASM</option>
          <option v-if="isFsmUser || isOtherUser" :value="ALL">All ASMs</option>
          <option v-for="asm in asmList" :key="asm.profile_id" :value="String(asm.profile_id)">
            {{ asm.name }}
          </option>
        </select>
      </div>

      <!-- Distributor -->
      <div class="dropdown-item">
        <label for="distributorSelect">Distributor</label>
        <select id="distributorSelect" v-model="selectedDistributor" :disabled="isDistributorLocked"
          @change="onDistributorChange">
          <option value="">Select Distributor</option>
          <option v-if="isFsmUser || isAsmUser || isOtherUser" :value="ALL">All Distributors</option>
          <option v-for="d in distributorList" :key="d.profile_id" :value="String(d.profile_id)">
            {{ d.name }}
          </option>
        </select>
      </div>

      <!-- ✅ Route -->
      <div class="dropdown-item">
        <label for="routeSelect">Route</label>
        <select id="routeSelect" v-model="selectedRoute"
          :disabled="!routeList.length || !selectedDistributor || selectedDistributor === ALL">
          <option value="">All Routes</option>
          <option v-for="r in routeList" :key="r.route_id ?? r.id" :value="String(r.route_id ?? r.id)">
            {{ r.route_name ?? r.name ?? ("Route #" + (r.route_id ?? r.id)) }}
          </option>
        </select>
      </div>

      <!-- Start Date -->
      <div class="dropdown-item">
        <label>Start Date</label>
        <input type="date" v-model="startDate" :max="currentDate" @change="handleDateChange"
          @blur="validateStartDate(true)" />
      </div>

      <!-- End Date -->
      <div class="dropdown-item">
        <label>End Date</label>
        <input type="date" v-model="endDate" :max="currentDate" @change="handleDateChange"
          @blur="validateEndDate(true)" />
      </div>

      <!-- ✅ Rep Name -->
      <div class="dropdown-item">
        <label for="repSelect">Rep Name</label>
        <select id="repSelect" v-model="selectedRep"
          :disabled="!repList.length || !selectedDistributor || selectedDistributor === ALL">
          <option value="">All Reps</option>
          <option v-for="r in repList" :key="r.profile_id ?? r.id" :value="String(r.profile_id ?? r.id)">
            {{ r.rep_name ?? r.name ?? r.first_name ?? ("Rep #" + (r.profile_id ?? r.id)) }}
          </option>
        </select>
      </div>

      <!-- Search -->
      <div class="dropdown-item search-container">
        <button @click="performSearch" class="btn button-default mt-4" :disabled="loading">
          {{ loading ? "Searching..." : "Search" }}
        </button>
      </div>

      <!-- Export -->
      <div class="dropdown-item search-container">
        <button @click="exportToExcel" class="btn button-export mt-4" :disabled="!reportData.length">
          Export to Excel
        </button>
      </div>
    </div>

    <!-- Loading -->
    <div v-if="loading" class="loading-spinner mt-3">
      <i class="fa fa-spinner fa-spin"></i> Loading...
    </div>

    <!-- ✅ render only when we have both items and data -->
    <div v-if="!loading && reportData.length && items.length" class="card mt-3">
      <div class="table-scroll">
        <table class="table table-striped table-bordered">
          <thead>
            <tr class="header-row-1">
              <th rowspan="2">Distributor</th>
              <th rowspan="2">Route</th>
              <th rowspan="2">Rep Name</th>
              <th rowspan="2">Date</th>

              <th :colspan="items.length" class="text-center">Products</th>

              <th rowspan="2">Total Qty</th>
            </tr>

            <tr class="header-row-2">
              <th v-for="it in items" :key="it.item_id" class="text-center">
                {{ it.item_name }}
              </th>
            </tr>
          </thead>

          <tbody>
            <tr v-for="(row, i) in reportData" :key="i">
              <td>{{ row.distributor_name || "-" }}</td>
              <td>{{ row.route_name || "-" }}</td>
              <td>{{ row.rep_name || "-" }}</td>
              <td>{{ row.invoice_date || "-" }}</td>

              <td v-for="it in items" :key="it.item_id" class="text-end">
                {{ formatQty(row?.[String(it.item_id)] ?? 0) }}
              </td>

              <td class="text-end">{{ formatQty(row.total_qty ?? 0) }}</td>
            </tr>

            <tr class="total-row">
              <td colspan="4" class="text-end"><strong>TOTAL</strong></td>

              <td v-for="it in items" :key="it.item_id" class="text-end">
                <strong>{{ formatQty(columnTotal(String(it.item_id))) }}</strong>
              </td>

              <td class="text-end"><strong>{{ formatQty(grandTotalQty) }}</strong></td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <div v-if="!loading && !reportData.length" class="no-data-message">
      <p>No data found. Please select filters and click Search.</p>
    </div>
  </div>
</template>

<script>
import * as XLSX from "xlsx";
import {
  fetchFsmList,
  fetchAsmList,
  fetchDistributorList,
  fetchDissaleReport,
  fetchRouteList,
  fetchSalesRepList,
} from "@/services/dataService";
import { useToast } from "vue-toastification";

export default {
  name: "DistributorWiseSalesForm",

  data() {
    const ALL = "__all__";
    const currentDate = new Date().toISOString().split("T")[0];

    return {
      token: "",

      fsmList: [],
      asmList: [],
      distributorList: [],

      // ✅ NEW: Route + Rep lists
      routeList: [],
      repList: [],

      selectedFsm: "",
      selectedAsm: "",
      selectedDistributor: "",

      // ✅ NEW: Route + Rep selections
      selectedRoute: "",
      selectedRep: "",

      startDate: currentDate,
      endDate: currentDate,
      currentDate,

      loading: false,

      // ✅ dynamic columns
      items: [],       // [{item_id,item_name,sort_id}]
      reportData: [],  // rows

      userLabel: "",
      roleKey: "OTHER",
      myProfileId: Number(localStorage.getItem("profile_id") || 0),

      isFsmLocked: false,
      isAsmLocked: false,
      isDistributorLocked: false,

      ALL,
      redirecting: false,
    };
  },

  computed: {
    isDistributorUser() { return this.roleKey === "DIS"; },
    isAsmUser() { return this.roleKey === "ASM"; },
    isFsmUser() { return this.roleKey === "FSM"; },
    isOtherUser() { return this.roleKey === "OTHER"; },

    grandTotalQty() {
      return this.reportData.reduce((sum, r) => sum + (Number(r.total_qty) || 0), 0);
    },
  },

  mounted() {
    this.initLoginAndLoadFsm();
  },

  methods: {
    toast(type, message) {
      const t = useToast();
      type === "error"
        ? t.error(message, { position: "top-right", timeout: 3000 })
        : t.success(message, { position: "top-right", timeout: 3000 });
    },



    isValidISODate(dateStr) {
      if (!dateStr || typeof dateStr !== "string") return false;

      // block manual formats like 2025/11/31
      if (dateStr.includes("/")) return false;

      // must be YYYY-MM-DD
      const m = dateStr.match(/^(\d{4})-(\d{2})-(\d{2})$/);
      if (!m) return false;

      const y = Number(m[1]);
      const mo = Number(m[2]);
      const d = Number(m[3]);

      // detect overflow dates like 2025-11-31
      const dt = new Date(y, mo - 1, d);
      return dt.getFullYear() === y && dt.getMonth() === mo - 1 && dt.getDate() === d;
    },

    validateStartDate(showToast = true) {
      if (!this.startDate) return true;

      if (!this.isValidISODate(this.startDate)) {
        if (showToast) this.toast("error", "Invalid Start Date");
        this.startDate = "";
        return false;
      }

      if (this.startDate > this.currentDate) {
        if (showToast) this.toast("error", "Start Date cannot be in the future");
        this.startDate = "";
        return false;
      }

      return true;
    },

    validateEndDate(showToast = true) {
      if (!this.endDate) return true;

      if (!this.isValidISODate(this.endDate)) {
        if (showToast) this.toast("error", "Invalid End Date");
        this.endDate = "";
        return false;
      }

      if (this.endDate > this.currentDate) {
        if (showToast) this.toast("error", "End Date cannot be in the future");
        this.endDate = "";
        return false;
      }

      return true;
    },

    handleDateChange() {
      // validate each field silently first
      this.validateStartDate(false);
      this.validateEndDate(false);

      // validate range if both exist
      if (this.startDate && this.endDate) {
        if (this.endDate < this.startDate) {
          this.toast("error", "End date cannot be before start date.");
          this.endDate = "";
        }
      }
    },

    validateDatesForSearch() {
      // REQUIRED
      if (!this.startDate) { this.toast("error", "Please select valid Start Date"); return false; }
      if (!this.endDate) { this.toast("error", "Please select valid End Date"); return false; }

      // VALID
      if (!this.validateStartDate(true)) return false;
      if (!this.validateEndDate(true)) return false;

      // RANGE
      if (this.endDate < this.startDate) {
        this.toast("error", "End date cannot be before start date.");
        return false;
      }

      return true;
    },


    // ========= AUTH helpers =========
    bodyOf(x) { return x?.data ?? x; },
    msgOf(x) {
      const b = this.bodyOf(x?.response?.data ?? x);
      return String(b?.message ?? b?.error ?? x?.message ?? "");
    },

    //   const http = x?.response?.status ?? x?.status;
    //   const b = this.bodyOf(x?.response?.data ?? x);
    //   const statusVal = String(b?.status ?? "").toLowerCase();
    //   const msg = this.msgOf(x).toLowerCase();

    //   return (
    //     http === 401 || http === 419 || http === 440 || http === 498 ||
    //     statusVal === "failed" ||
    //     /access token is invalid|unauthenticated|token.*expired|expired token|invalid token|session.*expired/.test(msg)
    //   );
    // },

    isUnauthorizedAny(x) {
      const http = x?.response?.status ?? x?.status;
      const msg = this.msgOf(x).toLowerCase();

      const authMsg =
        /access token is invalid|unauthenticated|token.*expired|expired token|invalid token|session.*expired/;

      // ✅ KEY FIX: only logout on 401 if message clearly indicates auth/token issue
      if (http === 401) return authMsg.test(msg);

      return (
        http === 419 ||
        http === 440 ||
        http === 498 ||
        authMsg.test(msg)
      );
    },


    redirectToLogin(message) {
      if (this.redirecting) return;
      this.redirecting = true;

      this.toast("error", message || "Session expired. Please login again.");

      localStorage.removeItem("token_set");
      localStorage.removeItem("profile_id");
      localStorage.removeItem("user_label");
      localStorage.removeItem("userLabel");
      localStorage.removeItem("role");

      if (this.$router) this.$router.replace("/login");
      else window.location.replace("/login");
    },
    handleUnauthorized(x) {
      if (!this.isUnauthorizedAny(x)) return false;
      this.redirectToLogin();
      return true;
    },

    initRoleFromStorage() {
      const labelFromStorage =
        localStorage.getItem("user_label") ||
        localStorage.getItem("userLabel") ||
        localStorage.getItem("role") ||
        "";

      this.userLabel = String(labelFromStorage);
      const labelUpper = String(this.userLabel || "").toUpperCase().trim();

      if (labelUpper === "DISTRIBUTOR" || labelUpper.startsWith("DISTRIBUTOR") || labelUpper === "DIS" || labelUpper.startsWith("DIS_")) {
        this.roleKey = "DIS";
      } else if (labelUpper === "ASM" || labelUpper.startsWith("ASM_")) {
        this.roleKey = "ASM";
      } else if (labelUpper === "FSM" || labelUpper.startsWith("FSM_")) {
        this.roleKey = "FSM";
      } else {
        this.roleKey = "OTHER";
      }
    },

    validateFiltersOneByOne() {
      if (!this.selectedFsm) { this.toast("error", "Please select FSM."); return false; }
      if (!this.selectedAsm) { this.toast("error", "Please select ASM."); return false; }
      if (!this.selectedDistributor) { this.toast("error", "Please select Distributor."); return false; }
      return true;
    },

    // ========= INIT =========
    async initLoginAndLoadFsm() {
      this.loading = true;
      try {
        const token = localStorage.getItem("token_set") || "";
        if (!token) return this.redirectToLogin("Session expired. Please login again.");

        this.token = token;
        this.myProfileId = Number(localStorage.getItem("profile_id") || 0);
        this.initRoleFromStorage();

        await this.loadFsmList();
        if (this.redirecting) return;

        this.selectedFsm = "";
        this.selectedAsm = "";
        this.selectedDistributor = "";

        this.selectedRoute = "";
        this.selectedRep = "";
        this.routeList = [];
        this.repList = [];

        this.asmList = [];
        this.distributorList = [];
        this.items = [];
        this.reportData = [];
      } catch (err) {
        if (this.handleUnauthorized(err)) return;
        console.error("[DistributorWiseSales] init error:", err?.response?.data || err);
        this.toast("error", "Failed to initialize page.");
      } finally {
        this.loading = false;
      }
    },

    // ========= DROPDOWNS =========


    async loadFsmList() {
      try {
        const profileId = localStorage.getItem("profile_id");
        const apiRes = await fetchFsmList(profileId, this.token);

        if (this.handleUnauthorized(apiRes)) return;
        const body = this.bodyOf(apiRes);
        if (this.handleUnauthorized(body)) return;

        this.fsmList = body?.data || [];
      } catch (err) {
        if (this.handleUnauthorized(err)) return;
        console.error(err);
        this.toast("error", "Failed to load FSM list.");
      }
    },


    async onFsmChange() {
      this.selectedAsm = "";
      this.selectedDistributor = "";
      this.asmList = [];
      this.distributorList = [];
      this.isDistributorLocked = false;

      // clear optional filters + report
      this.selectedRoute = "";
      this.selectedRep = "";
      this.routeList = [];
      this.repList = [];
      this.items = [];
      this.reportData = [];

      if (this.selectedFsm === this.ALL) return;
      if (!this.selectedFsm) return;

      await this.loadAsmList();
    },

    async onAsmChange() {
      this.selectedDistributor = "";
      this.distributorList = [];
      this.isDistributorLocked = false;

      // clear optional filters + report
      this.selectedRoute = "";
      this.selectedRep = "";
      this.routeList = [];
      this.repList = [];
      this.items = [];
      this.reportData = [];

      if (this.selectedAsm === this.ALL) return;
      if (!this.selectedAsm) return;

      await this.loadDistributorList();
    },


    //   try {
    //     const profileId = localStorage.getItem("profile_id");
    //     const res = await fetchAsmList(profileId, this.selectedFsm, this.token);
    //     if (this.handleUnauthorized(res)) return;

    //     const body = this.bodyOf(res);
    //     this.asmList = body?.data || [];
    //   } catch (err) {
    //     if (this.handleUnauthorized(err)) return;
    //     console.error(err);
    //     this.toast("error", "Failed to load ASM list.");
    //   }
    // },

    async loadAsmList() {
      try {
        const profileId = localStorage.getItem("profile_id");
        const apiRes = await fetchAsmList(profileId, this.selectedFsm, this.token);

        if (this.handleUnauthorized(apiRes)) return;
        const body = this.bodyOf(apiRes);
        if (this.handleUnauthorized(body)) return;

        const list = body?.data || [];
        this.asmList = list;

        if (!list.length) {

          this.selectedAsm = "";
          this.selectedDistributor = "";
          this.distributorList = [];
          this.routeList = [];
          this.repList = [];
          return;
        }
      } catch (err) {
        if (this.handleUnauthorized(err)) return;
        console.error(err);
        this.toast("error", "Failed to load ASM list.");
      }
    },



    async loadDistributorList() {
      try {
        const profileId = localStorage.getItem("profile_id");
        const apiRes = await fetchDistributorList(profileId, this.selectedAsm, this.token);

        if (this.handleUnauthorized(apiRes)) return;
        const body = this.bodyOf(apiRes);
        if (this.handleUnauthorized(body)) return;

        let list = body?.data || [];

        if (!list.length) {

          this.selectedDistributor = "";
          this.distributorList = [];
          this.routeList = [];
          this.repList = [];
          return;
        }

        // Distributor login lock
        if (this.isDistributorUser && this.myProfileId) {
          const mine = list.find(d => Number(d.profile_id) === Number(this.myProfileId));
          if (mine) {
            list = [mine];
            this.selectedDistributor = String(mine.profile_id);
            this.isDistributorLocked = true;

            // programmatic set doesn't trigger @change
            await Promise.all([this.loadRouteList(), this.loadRepList()]);
          }
        }

        this.distributorList = list;
      } catch (err) {
        if (this.handleUnauthorized(err)) return;
        console.error(err);
        this.toast("error", "Failed to load Distributor list.");
      }
    },


    async onDistributorChange() {
      this.selectedRoute = "";
      this.selectedRep = "";
      this.routeList = [];
      this.repList = [];
      this.items = [];
      this.reportData = [];

      if (!this.selectedDistributor) return;
      if (this.selectedDistributor === this.ALL) return;

      await Promise.all([this.loadRouteList(), this.loadRepList()]);
    },




    async loadRouteList() {
      try {
        if (!this.selectedDistributor || this.selectedDistributor === this.ALL) {
          this.routeList = [];
          return;
        }

        const apiRes = await fetchRouteList(this.selectedDistributor, this.token);

        if (this.handleUnauthorized(apiRes)) return;
        const body = this.bodyOf(apiRes);
        if (this.handleUnauthorized(body)) return;

        // adjust if your API shape differs
        this.routeList = body?.data?.data ?? body?.data ?? [];
      } catch (err) {
        if (this.handleUnauthorized(err)) return;
        console.error(err?.response?.data || err);
        this.routeList = [];
      }
    },

    async loadRepList() {
      try {
        if (!this.selectedDistributor || this.selectedDistributor === this.ALL) {
          this.repList = [];
          return;
        }

        const apiRes = await fetchSalesRepList(this.selectedDistributor, this.token);

        if (this.handleUnauthorized(apiRes)) return;
        const body = this.bodyOf(apiRes);
        if (this.handleUnauthorized(body)) return;

        this.repList = body?.data?.data ?? body?.data ?? [];
      } catch (err) {
        if (this.handleUnauthorized(err)) return;
        console.error(err?.response?.data || err);
        this.repList = [];
      }
    },



    //   try {
    //     if (!this.selectedDistributor || this.selectedDistributor === this.ALL) return;

    //     const res = await fetchSalesRepList(this.selectedDistributor, this.token);
    //     if (this.handleUnauthorized(res)) return;

    //     const body = this.bodyOf(res);
    //     this.repList = body?.data?.data ?? body?.data ?? [];
    //   } catch (err) {
    //     if (this.handleUnauthorized(err)) return;
    //     console.error("[RepList] error:", err?.response?.data || err);
    //     this.repList = [];
    //   }
    // },

    // ========= FORMATTERS =========



    formatQty(v) {
      const n = Number(v);
      if (!Number.isFinite(n)) return "0";
      return n.toLocaleString("en-US", { maximumFractionDigits: 0 });
    },

    columnTotal(itemIdStr) {
      return this.reportData.reduce((sum, r) => sum + (Number(r?.[itemIdStr]) || 0), 0);
    },



    async performSearch() {
      this.token = localStorage.getItem("token_set") || "";
      if (!this.token) return this.redirectToLogin();

      if (!this.validateFiltersOneByOne()) return;
      if (!this.validateDatesForSearch()) return;


      this.loading = true;
      this.items = [];
      this.reportData = [];

      try {
        const payload = {
          start_date: this.startDate,
          end_date: this.endDate,
          fsm_profile_id: this.selectedFsm === this.ALL ? null : this.selectedFsm,
          asm_profile_id: this.selectedAsm === this.ALL ? null : this.selectedAsm,
          distributor_profile_id: this.selectedDistributor === this.ALL ? null : this.selectedDistributor,
          route_id: this.selectedRoute || null,
          rep_profile_id: this.selectedRep || null,
        };

        const apiRes = await fetchDissaleReport(this.token, payload);

        if (this.handleUnauthorized(apiRes)) return;
        const res = this.bodyOf(apiRes);
        if (this.handleUnauthorized(res)) return;

        if (!res || res.status !== true) {
          this.toast("error", res?.message || "Error loading report.");
          return;
        }

        const items = Array.isArray(res.items) ? res.items : [];
        const rows = Array.isArray(res.data) ? res.data : [];

        this.items = items
          .map(x => ({
            item_id: String(x.item_id),
            item_name: String(x.item_name ?? ""),
            sort_id: Number(x.sort_id ?? 0),
          }))
          .sort((a, b) => a.sort_id - b.sort_id);

        this.reportData = rows.map(r => {
          const out = { ...r };
          this.items.forEach(it => {
            const k = String(it.item_id);
            out[k] = Number(r?.[k]) || 0;
          });
          out.total_qty = Number(r?.total_qty) || 0;
          return out;
        });

        if (!this.reportData.length) {
          this.toast("error", "No data found for the selected filters.");
          return;
        }

        this.toast("success", "Report loaded successfully.");
      } catch (err) {
        if (this.handleUnauthorized(err)) return;
        console.error(err?.response?.data || err);
        this.toast("error", err?.response?.data?.message || "Error loading report.");
      } finally {
        this.loading = false;
      }
    },


    // ========= EXPORT =========
    exportToExcel() {
      if (!this.reportData.length || !this.items.length) {
        this.toast("error", "No data to export.");
        return;
      }

      // helper to get filter names
      const getName = (list, id, field = "name") => {
        if (!id || id === this.ALL) return "All";
        const item = list.find(i => String(i.profile_id ?? i.id ?? i.route_id) === String(id));
        return item ? (item[field] || item.route_name || item.name) : "";
      };

      // ===== FILTER SECTION =====
      const filterSection = [
        ["Distributor Wise Sales Report"],
        [],
        ["FSM", getName(this.fsmList, this.selectedFsm)],
        ["ASM", getName(this.asmList, this.selectedAsm)],
        ["Distributor", getName(this.distributorList, this.selectedDistributor)],
        ["Route", getName(this.routeList, this.selectedRoute, "route_name")],
        ["Rep Name", getName(this.repList, this.selectedRep, "rep_name")],
        ["Start Date", this.startDate],
        ["End Date", this.endDate],
        [],
      ];

      // ===== HEADERS =====
      const header1 = [
        "Distributor",
        "Route",
        "ASM/FSM/SE/Rep name",
        "Date",
        "Products",
        ...Array(Math.max(this.items.length - 1, 0)).fill(""),
        "Total Qty",
      ];

      const header2 = ["", "", "", "", ...this.items.map(it => it.item_name), ""];

      const dataRows = this.reportData.map(r => ([
        r.distributor_name || "-",
        r.route_name || "-",
        r.rep_name || "-",
        r.invoice_date || "-",
        ...this.items.map(it => Number(r[String(it.item_id)]) || 0),
        Number(r.total_qty) || 0,
      ]));

      const totalRow = [
        "TOTAL", "", "", "",
        ...this.items.map(it => this.columnTotal(String(it.item_id))),
        this.grandTotalQty,
      ];

        const firstName = localStorage.getItem("first_name") || "";
        const lastName = localStorage.getItem("last_name") || "";
        const generatedBy = `${firstName} ${lastName}`.trim();
        const generatedAt = new Date().toLocaleString();

        const generatedRows = [
          [],
          ["Generated By", generatedBy],
          ["Generated At", generatedAt],
        ];

      const aoa = [...filterSection, header1, header2, ...dataRows, totalRow, ...generatedRows];
      const ws = XLSX.utils.aoa_to_sheet(aoa);
      const startRow = filterSection.length;

      ws["!merges"] = [
        { s: { r: startRow, c: 0 }, e: { r: startRow + 1, c: 0 } },
        { s: { r: startRow, c: 1 }, e: { r: startRow + 1, c: 1 } },
        { s: { r: startRow, c: 2 }, e: { r: startRow + 1, c: 2 } },
        { s: { r: startRow, c: 3 }, e: { r: startRow + 1, c: 3 } },
        { s: { r: startRow, c: 4 }, e: { r: startRow, c: 4 + this.items.length - 1 } },
        { s: { r: startRow, c: 4 + this.items.length }, e: { r: startRow + 1, c: 4 + this.items.length } },
      ];

      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Distributor_Wise_Sales");
      XLSX.writeFile(wb, `Distributor_Wise_Sales_${this.startDate}_to_${this.endDate}.xlsx`);
      this.toast("success", "Excel exported successfully.");
    },
  },
};
</script>

<style scoped>
/* ===== Layout ===== */
.container {
  background-color: #ffffff;
  box-shadow: 0 0 15px rgba(0, 0, 0, 0.1);
  border-radius: 8px;
  padding: 20px;
  margin: 10px;
  display: flex;
  flex-direction: column;
  min-height: calc(100vh - 20px);
  width: calc(100% - 20px);
  box-sizing: border-box;
}

.dropdown-container {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 15px;
  width: 100%;
  margin-bottom: 20px;
}

.dropdown-item {
  min-width: 180px;
  max-width: 250px;
}

label {
  display: block;
  margin-bottom: 5px;
  font-weight: 700;
  font-size: 14px;
}

select,
input[type="date"] {
  width: 100%;
  padding: 10px;
  border-radius: 5px;
  border: 1px solid #ccc;
  font-size: 14px;
}

.search-container {
  display: flex;
  align-items: flex-end;
  justify-content: left;
}

.btn.button-default {
  padding: 10px 20px;
  border: none;
  border-radius: 5px;
  background-color: #007bff;
  color: #ffffff;
  font-size: 14px;
  cursor: pointer;
}

.btn.button-default:disabled {
  background-color: #6c757d;
  cursor: not-allowed;
}

.btn.button-export {
  padding: 10px 20px;
  border: none;
  border-radius: 5px;
  background-color: #28a745;
  color: #ffffff;
  font-size: 14px;
  cursor: pointer;
}

.btn.button-export:disabled {
  background-color: #6c757d;
  cursor: not-allowed;
}

.loading-spinner {
  font-size: 18px;
  text-align: center;
  margin-top: 20px;
  color: #007bff;
}

/* ===== Table Scroll + Sticky Header (no overlap) ===== */
.table-scroll {
  max-height: 500px;
  overflow-y: auto;
  overflow-x: auto;
  border: 1px solid #ddd;
  margin-top: 10px;
  position: relative;
}

.table {
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
}

.table th,
.table td {
  padding: 12px;
  border-bottom: 1px solid #ddd;
  white-space: nowrap;
}

.table thead th {
  background: #f9f9f9;
  font-weight: 700;
  line-height: 20px;
  height: 44px;
  vertical-align: middle;
  z-index: 1 !important;
}

.table thead .header-row-1 th {
  position: sticky;
  top: 0;
  z-index: 30;
}

.table thead .header-row-2 th {
  position: sticky;
  top: 44px;
  z-index: 20;
}

.total-row td {
  background: #f1f3f5;
  font-weight: 700;
  border-top: 2px solid #495057;
}

.text-center {
  text-align: center;
}

.text-end {
  text-align: right;
}

.card {
  width: 100%;
  background-color: #ffffff;
  border-radius: 8px;
  box-shadow: 0 0 10px rgba(0, 0, 0, 0.1);
}

.no-data-message {
  text-align: center;
  padding: 40px;
  font-size: 16px;
  color: #666;
}
</style>
